import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useUser } from "MVVM/Hooks/useUser";
import { FeedbackModal } from "MVVM/Components/FeedbackModal";
import { Panel } from "./Panel";
import { StatusPill } from "./StatusPill";
import { AuthPrimary, AuthSecondary, Cluster } from "./styledComponents";

const PANEL_ID = "site-nav-user-menu";
// Matches AuthenticatedSection: these routes should not bounce you back.
const EXCLUDED_NEXT = ["/start"];

/**
 * The "what's mine" half of the bar. Deliberately does not reuse the old
 * Tooltip-based dropdown: that has no Esc, no focus handling, and a trigger
 * that is not a button. This mirrors SiteNav's own open/close behaviour.
 */
export const UserMenu = () => {
  const { allstarUser } = useUser();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const clusterRef = useRef<HTMLDivElement>(null);

  // Navigating with the panel open would leave it hanging over the new page.
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    const onPointerDown = (event: MouseEvent) => {
      if (!clusterRef.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  if (!allstarUser?.loggedIn) {
    const loginUrl = EXCLUDED_NEXT.includes(pathname)
      ? "/login"
      : `/login?next=${pathname}`;

    return (
      <Cluster>
        <AuthSecondary to={loginUrl}>Log in</AuthSecondary>
        <AuthPrimary to="/register">Sign up free</AuthPrimary>
      </Cluster>
    );
  }

  return (
    <Cluster ref={clusterRef}>
      <StatusPill
        ref={triggerRef}
        open={open}
        panelId={PANEL_ID}
        onClick={() => setOpen((previous) => !previous)}
      />
      {open && (
        <Panel
          panelId={PANEL_ID}
          onNavigate={() => setOpen(false)}
          onOpenFeedback={() => {
            setOpen(false);
            setFeedbackOpen(true);
          }}
        />
      )}
      <FeedbackModal
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        hideTrigger
        topic="new-nav"
      />
    </Cluster>
  );
};
